import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoHappyOutline } from 'react-icons/io5';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { playTeaSound, playCoffeeSound, playRocketSound, playCallMeSound } from '../utils/soundEffects';
import teaArt from '../images/tea.svg';

// Reaction values, in tray order, and they must match the server's own
// allow-list (socketServer.js's REACTION_EMOJIS) - anything not on it is
// rejected there, so a mismatch just produces buttons that do nothing.
//
// 'tea' is a named value rather than a glyph: no Unicode emoji is a Turkish
// tea (U+1F375 is a Japanese green tea cup), so it ships as its own artwork
// and every render site below has to branch on it.
const TEA = 'tea';
const COFFEE = '☕';
const REACTIONS = [TEA, COFFEE, '😂', '🔥', '❤️', '👍', '👎', '🤙🏻', '🎉', '😮', '😭', '💀', '🧠', '🚀'];

// Reactions that also play a sound for everyone in the room.
const REACTION_SOUNDS = { [TEA]: playTeaSound, [COFFEE]: playCoffeeSound, '🚀': playRocketSound, '🤙🏻': playCallMeSound };

// How long a thrown emoji stays on screen. Kept in sync with the
// roomReactionFly animation in Groups.css - the element is removed by this
// timer, so a shorter animation would leave an invisible node sitting in the
// layer and a longer one would be cut off mid-flight.
const FLIGHT_MS = 2600;

export default function RoomReactions({ roomId }) {
  const { sendReaction, onReaction } = useSocket();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [flying, setFlying] = useState([]);
  // Lets the cleanup below clear timers for reactions still in flight when
  // the room is left, rather than setting state on an unmounted component.
  const timersRef = useRef(new Set());

  useEffect(() => {
    const unsubscribe = onReaction((reaction) => {
      if (REACTION_SOUNDS[reaction.value]) REACTION_SOUNDS[reaction.value]();
      // Randomised so a burst of the same emoji doesn't fly in a single
      // stacked column - each one gets its own lane and drift.
      const item = {
        ...reaction,
        left: 8 + Math.random() * 76,
        drift: `${Math.round(-40 + Math.random() * 80)}px`,
        delay: `${Math.round(Math.random() * 120)}ms`,
        scale: 0.85 + Math.random() * 0.5
      };
      setFlying((prev) => [...prev, item]);
      const timer = setTimeout(() => {
        setFlying((prev) => prev.filter((f) => f.reactionId !== item.reactionId));
        timersRef.current.delete(timer);
      }, FLIGHT_MS);
      timersRef.current.add(timer);
    });
    return unsubscribe;
  }, [onReaction]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const throwEmoji = useCallback((value) => {
    sendReaction(roomId, 'emoji', value);
    setOpen(false);
  }, [roomId, sendReaction]);

  return (
    <>
      <div className="RoomReactions">
        <button
          className={`RoomReactionsToggle ${open ? 'primaryColor' : 'secondaryColor'}`}
          onClick={() => setOpen((prev) => !prev)}
          title={t('reactions.title')}
        >
          <IoHappyOutline /> {t('reactions.title')}
        </button>

        {open && (
          <div className="RoomReactionsTray">
            <div className="RoomReactionsGrid">
              {REACTIONS.map((value) => (
                <button
                  key={value}
                  className="RoomReactionsEmoji"
                  onClick={() => throwEmoji(value)}
                  title={value === TEA ? t('reactions.tea') : value === COFFEE ? t('reactions.coffee') : t('reactions.throw')}
                >
                  {value === TEA ? <img src={teaArt} alt={t('reactions.tea')} className="RoomReactionArt" /> : value}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Viewport-fixed and pointer-events:none (see Groups.css) so a flight
          in progress can never swallow a click meant for the UI underneath. */}
      <div className="RoomReactionLayer">
        {flying.map((f) => (
          <span
            key={f.reactionId}
            className="RoomReactionFly"
            style={{
              left: `${f.left}%`,
              animationDelay: f.delay,
              '--reaction-drift': f.drift,
              '--reaction-scale': f.scale
            }}
          >
            <span className="RoomReactionGlyph">
              {f.value === TEA ? <img src={teaArt} alt="" className="RoomReactionFlyArt" /> : f.value}
            </span>
            <span className="RoomReactionWho">{f.username}</span>
          </span>
        ))}
      </div>
    </>
  );
}
